import React, { useState, FC } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  makeStyles,
  Typography,
  Grid
} from '@material-ui/core';
import {
  LocationOnOutlined,
  PhoneOutlined,
  EmailOutlined
} from '@material-ui/icons';
import { useOrdering } from '../../../stores/OrderingStore';
import BizHour from '../BizHour/BizHour';
import { isNullOrUndefined } from 'util';
import { OnlineOrderingStore } from 'kiolyn-api';
import visa from 'payment-icons/min/flat/visa.svg';
import master from 'payment-icons/min/flat/mastercard.svg';
import amex from 'payment-icons/min/flat/amex.svg';
import discover from 'payment-icons/min/flat/discover.svg';
import { LayoutVariant } from '../../Core/CommonTypes';
import AuthSection from '../../Auth/AuthSection';
import AuthDialog from '../../Auth/AuthDialog/AuthDialog';
import Logo from '../Logo';
import Clock from './Clock';
import { defaultTimezone } from '../../../domain/models/Constants';
import moment from 'moment-timezone';

const useStyles = makeStyles(theme => ({
  content: {
    padding: theme.spacing(1),
    display: 'flex'
  },
  wrapper: {
    flex: 1
  },
  title: {
    marginRight: 48
  },
  bizHour: {
    marginBottom: theme.spacing(2),
    textAlign: 'center'
  },
  close: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1)
  },
  menuTitle: {
    lineHeight: 1.2,
    margin: theme.spacing(2)
  },
  header: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    borderBottom: '1px solid grey'
  },
  headerMobile: {
    display: 'flex',
    borderBottom: '1px solid grey',
    flexDirection: 'column'
    // textAlign: 'left'
  },
  authSectionMobile: {
    marginBottom: 10
  },
  authSection: {
    position: 'absolute',
    right: 10,
    bottom: 16
  },
  paddindClass: {
    paddingTop: theme.spacing(2.5),
    paddingBottom: theme.spacing(2.5)
  },
  imgsvg: {
    height: '5%',
    width: '5%',
    margin: theme.spacing(1) / 2
  },
  imgmobilesvg: {
    height: '12%',
    width: '12%',
    margin: theme.spacing(1) / 2
  },
  buttonMargin: {
    margin: theme.spacing(1) / 2
  },
  icon: {
    marginRight: theme.spacing(1)
  },
  phoneNumber: {
    textDecoration: 'none',
    color: 'rgba(0, 0, 0, 0.87)'
  }
}));

interface InfoDialogProps {
  onClose?: () => void;
  onClick?: () => void;
  variant?: LayoutVariant;
  validatedDate?: Date;
}

const InfoDialog: FC<InfoDialogProps> = ({
  onClose,
  onClick,
  variant,
  validatedDate
}) => {
  const classes = useStyles();
  const orderingStore = useOrdering();

  // need merge pull count down first and can update api-specs
  const store = orderingStore.store as OnlineOrderingStore & {
    pause_online_ordering_before: string;
    is_show_pause_online_ordering?: boolean;
  };
  // const user = orderingStore.onlineUser as OnlineUser;

  const formatPhoneNumber = (value: string) => {
    const rawtel = value.replace(/-/g, '');
    return `${rawtel.substring(0, 3)}-${rawtel.substring(
      3,
      6
    )}-${rawtel.substring(6, 10)}`;
  };

  const cards: { [name: string]: string } = { visa, master, amex, discover };

  const [showDialogLogin, setShowDialogLogin] = useState(false);

  const handleOnclick = (value: string) => {
    if (
      !isNullOrUndefined(
        store.delivery_settings?.third_party_delivery_button_link
      ) &&
      store.delivery_settings?.third_party_delivery_button_link !== '' &&
      value === 'delivery_third_party'
    ) {
      window.open(
        store.delivery_settings?.third_party_delivery_button_link!,
        '_blank'
      );
      return;
    }
    orderingStore.order.order_type = value;
    if (onClick !== undefined) {
      onClick();
    }
  };
  const isMobile = variant === LayoutVariant.mobile;
  const holidays = store.holidays;
  const currentHoliday = holidays?.length
    ? holidays.find(e => e?.date === moment().format('YYYY-MM-DD'))
    : null;
  const holidayText = currentHoliday
    ? `${currentHoliday.name}. ${
        currentHoliday.is_working
          ? currentHoliday.from_1 && currentHoliday.to_1
            ? `We open today from ${currentHoliday.from_1} to ${currentHoliday.to_1}`
            : ''
          : 'We closed today'
      }`
    : null;
  const timezone = store.timezone || defaultTimezone;
  const isShowPauseOnlineOrdering = store.is_show_pause_online_ordering;

  /* eslint-disable */
  return (
    <Dialog open onClose={onClose} fullScreen scroll="paper">
      <DialogContent dividers classes={{ root: classes.content }}>
        <Box width="100%" justifyContent="center" className={classes.bizHour}>
          <div
            className={
              store.is_show_auth
                ? isMobile
                  ? classes.headerMobile
                  : classes.header
                : undefined
            }
          >
            <Logo variant="h3" textClassName={classes.menuTitle} />
            {store.is_show_auth ? (
              <div
                className={
                  isMobile ? classes.authSectionMobile : classes.authSection
                }
              >
                <AuthSection
                  name={orderingStore.order.name}
                  onClick={() => setShowDialogLogin(true)}
                />
              </div>
            ) : null}
          </div>

          {orderingStore.store.biz_hours && (
            <Box className={classes.paddindClass}>
              <BizHour
                variant="vertical"
                bizHours={orderingStore.store.biz_hours}
                validatedDate={validatedDate}
                holidays={orderingStore.store.holidays}
                timezone={orderingStore.store.timezone}
              />
            </Box>
          )}
          {!isNullOrUndefined(store.info_page_settings?.intro_text_1) &&
            store.info_page_settings?.enable_text_line && (
              <Typography variant="h6" className={classes.paddindClass}>
                {store.info_page_settings?.intro_text_1}
                {!isNullOrUndefined(store.info_page_settings?.intro_text_1) && (
                  <br />
                )}
                {store.info_page_settings?.intro_text_2}
              </Typography>
            )}

          {!holidayText || <Typography variant="h6">{holidayText}</Typography>}
          <Grid
            container
            direction="row"
            justify="center"
            className={classes.paddindClass}
          >
            {store.accept_delivery &&
              !isNullOrUndefined(
                store.delivery_settings?.third_party_delivery_button_text
              ) &&
              store.delivery_settings?.delivery_type === 'third_party' && (
                <Button
                  className={classes.buttonMargin}
                  onClick={() => {
                    handleOnclick('delivery_third_party');
                  }}
                  variant="contained"
                  color="primary"
                  size="large"
                >
                  {store.delivery_settings?.third_party_delivery_button_text}
                </Button>
              )}
            {store.accept_dinein && (
              <Button
                className={classes.buttonMargin}
                onClick={() => {
                  handleOnclick('dinein');
                }}
                variant="contained"
                color="primary"
                size="large"
              >
                {store.info_page_settings?.dinein_button_text === ''
                  ? 'Start a Dine-In order'
                  : store.info_page_settings?.dinein_button_text}
              </Button>
            )}
            {store.accept_delivery &&
              store.delivery_settings?.delivery_type !== 'third_party' && (
                <Button
                  className={classes.buttonMargin}
                  onClick={() => {
                    handleOnclick('delivery');
                  }}
                  variant="contained"
                  color="primary"
                  size="large"
                >
                  {store.info_page_settings?.delivery_button_text === ''
                    ? 'Start a delivery order'
                    : store.info_page_settings?.delivery_button_text}
                </Button>
              )}
            {store.accept_pickup && (
              <Button
                className={classes.buttonMargin}
                onClick={() => {
                  handleOnclick('pickup');
                }}
                variant="contained"
                color="primary"
                size="large"
              >
                {store.info_page_settings?.pickup_button_text === ''
                  ? 'Start a pick-up order'
                  : store.info_page_settings?.pickup_button_text}
              </Button>
            )}
          </Grid>
          {isShowPauseOnlineOrdering && (
            <Grid
              container
              direction="row"
              justify="center"
              style={{ marginBottom: 20 }}
            >
              <Typography variant="h6">
                Online Ordering is not available until{' '}
                {moment
                  .utc(store.pause_online_ordering_before)
                  .tz(timezone)
                  .format('YYYY-MM-DD HH:mm:ss')}
              </Typography>
            </Grid>
          )}

          {store.info_page_settings?.enable_store_address && (
            <Grid container direction="row" justify="center">
              <LocationOnOutlined color="primary" className={classes.icon} />
              <Typography>{store.address}</Typography>
            </Grid>
          )}

          {store.info_page_settings?.enable_store_phone &&
            !isNullOrUndefined(store.phone) && (
              <Grid container direction="row" justify="center">
                <PhoneOutlined color="primary" className={classes.icon} />
                <Typography>
                  <a
                    target="_blank"
                    className={classes.phoneNumber}
                    href={`tel:${formatPhoneNumber(store.phone)}`}
                  >
                    {formatPhoneNumber(store.phone)}
                  </a>
                </Typography>
              </Grid>
            )}

          {store.info_page_settings?.enable_store_email &&
            !isNullOrUndefined(store.info_page_settings?.store_email) && (
              <Grid container direction="row" justify="center">
                <EmailOutlined color="primary" className={classes.icon} />
                <Typography>{store.info_page_settings?.store_email}</Typography>
              </Grid>
            )}

          {store.info_page_settings?.enable_payment_icon && (
            <Grid
              container
              direction="row"
              justify="center"
              className={classes.paddindClass}
            >
              {Object.keys(cards).map(key => (
                <img
                  src={cards[key]}
                  alt={key}
                  key={key}
                  className={
                    variant === LayoutVariant.mobile
                      ? classes.imgmobilesvg
                      : classes.imgsvg
                  }
                />
              ))}
            </Grid>
          )}
          <Clock timezone={timezone} />
          <Grid
            container
            direction="row"
            justify="center"
            className={classes.paddindClass}
          >
            <AuthDialog
              open={showDialogLogin}
              onClose={() => setShowDialogLogin(false)}
              variant={variant}
            />
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
export default InfoDialog;
